import React from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { Check } from 'lucide-react';
import { popIn, spring } from '../motion';

interface ChoiceProps {
  selected: boolean;
  onClick: () => void;
  image?: string | null;
  icon?: React.ReactNode;
  title: string;
  /** ロゴなど切り抜きたくない画像 */
  contain?: boolean;
}

/** 画像つきで選ぶカード（商品・スタイルなど）。 */
export const ChoiceCard: React.FC<ChoiceProps> = ({ selected, onClick, image, icon, title, contain }) => (
  <motion.button
    className="v2-choice"
    aria-pressed={selected}
    onClick={onClick}
    variants={popIn}
    whileHover={{ y: -3 }}
    whileTap={{ scale: 0.97 }}
    layout
  >
    <span className={`v2-choice-media${contain ? ' v2-choice-media--contain' : ''}`}>
      {image ? <img src={image} alt="" loading="lazy" /> : icon}
    </span>
    <span className="v2-choice-title">{title}</span>
    <AnimatePresence>
      {selected && (
        <motion.span
          className="v2-choice-check"
          initial={{ scale: 0, rotate: -45 }}
          animate={{ scale: 1, rotate: 0 }}
          exit={{ scale: 0 }}
          transition={spring}
        >
          <Check size={16} strokeWidth={3} />
        </motion.span>
      )}
    </AnimatePresence>
  </motion.button>
);

/** 小さな札で選ぶ（サイズ・本数・秒数など）。 */
export function Pills<T extends string | number>({
  options,
  value,
  onChange,
  render,
}: {
  options: { value: T; label: string }[];
  value: T;
  onChange: (v: T) => void;
  render?: (o: { value: T; label: string }) => React.ReactNode;
}) {
  return (
    <div className="v2-pills" role="group">
      {options.map((o) => (
        <motion.button
          key={String(o.value)}
          className="v2-pill"
          aria-pressed={o.value === value}
          onClick={() => onChange(o.value)}
          whileTap={{ scale: 0.95 }}
        >
          {render ? render(o) : o.label}
        </motion.button>
      ))}
    </div>
  );
}

/** 縦横比を小さな枠で見せる。 */
export const RatioIcon: React.FC<{ w: number; h: number }> = ({ w, h }) => {
  const max = 16;
  const s = max / Math.max(w, h);
  return <span className="v2-ratio" style={{ width: Math.max(6, w * s), height: Math.max(6, h * s) }} />;
};
